import React from 'react';
import { Box, CircularProgress, ImageListItem, ImageListItemBar } from '@mui/material';
import Image from 'components/common/image';
import type { DrawingType } from '../../types';
import type { DisplayedFile } from '../../../gallery/filesStore';
import { thumbnailService } from 'services/fileService';
import {
  getListImageContentSx,
  getListItemSx,
  listImageAnimatedActionIconSx,
  listImageBarActionsBottomSx,
  listImageBarSx,
  listImageBarFullTitleSx,
  listImageSpinnerContainerSx,
  focusedThumbnailSx,
  selectedThumbnailOutlineSx,
  selectedThumbnailOverlaySx,
} from '../../styles';

interface ImageListItemProps {
  image: DisplayedFile;
  drawing: DrawingType;
  thumbnailSize: number;
  listItemRef: React.RefObject<HTMLLIElement>;
  onClick: (e: React.SyntheticEvent) => void;
  showActions: boolean;
  setShowActions: React.Dispatch<React.SetStateAction<boolean>>;
  actionIcon: React.ReactNode;
  description: string;
  showFullTitle: boolean;
  error: boolean;
  isLoaded: boolean;
  setLoaded: () => void;
  onError: () => void;
  imageClassName: string;
  selected?: boolean;
  focused?: boolean;
}

export const ImageFileListItem: React.FC<ImageListItemProps> = ({
  image,
  drawing,
  thumbnailSize,
  listItemRef,
  onClick,
  showActions,
  setShowActions,
  actionIcon,
  description,
  showFullTitle,
  error,
  isLoaded,
  setLoaded,
  onError,
  imageClassName,
  selected = false,
  focused = false,
}) => {
  const src = thumbnailService.resolveListImageSrc(error, drawing, image);
  const { showSpinner, imageOpacity } =
    thumbnailService.resolveListImageDisplayState({
      src,
      isLoaded,
      error,
    });

  const resolvedActionIcon = showFullTitle ? (
    <Box sx={listImageAnimatedActionIconSx(showActions)}>{actionIcon}</Box>
  ) : showActions ? (
    actionIcon
  ) : undefined;

  return (
    <ImageListItem
      ref={listItemRef}
      sx={[getListItemSx(thumbnailSize, true), selected && selectedThumbnailOutlineSx, focused && focusedThumbnailSx]}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {selected && <Box sx={selectedThumbnailOverlaySx} />}
      {showSpinner && (
        <Box sx={listImageSpinnerContainerSx}>
          <CircularProgress />
        </Box>
      )}
      <Box sx={getListImageContentSx(imageOpacity)}>
        {src !== null && (
          <Image
            className={imageClassName}
            imageRotation={image.imageRotation}
            description={description}
            src={src}
            setLoaded={setLoaded}
            onError={onError}
            onClick={onClick}
          />
        )}
      </Box>
      <ImageListItemBar
        title={description}
        actionIcon={resolvedActionIcon}
        onClick={onClick}
        sx={
          showFullTitle
            ? [listImageBarSx, listImageBarFullTitleSx, listImageBarActionsBottomSx]
            : listImageBarSx
        }
      />
    </ImageListItem>
  );
};
